import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { debounce } from "lodash";

import SearchBar from "./SearchBar";
import ResultList from "./ResultList";
import FilterOptions from "./FilterOptions";
import GotoAll from "./GotoAll";
import Legends from "./Legends";
import RecentView from "./RecentView";
import "./Style/OpenSearch.scss";

import {
  REPORT_TYPE_OPTIONS,
  PUBLISHED_DATE_OPTIONS,
  FILTER_BY_TYPE_OPTIONS,
} from "./Constant";
import { OpenSearchService } from "./Service/OpenSearchService";
import constants from "../../Utils/constants";
import Toast from "../../Utils/Toast";

const OpenSearch = () => {
  const history = useHistory();

  // Core states
  const [searchInput, setSearchInput] = useState("");
  const [selectedDateFilter, setSelectedDateFilter] = useState("any-time");
  const [filterByTypeFilter, setFilterByTypeFilter] = useState("all");

  const [trendingKeywords, setTrendingKeywords] = useState([]);
  const [recentViewData, setRecentViewData] = useState([]);
  const [options, setOptions] = useState(REPORT_TYPE_OPTIONS);
  const [filterByTypeOptions, setFilterByTypeOptions] = useState(
    FILTER_BY_TYPE_OPTIONS
  );

  const [searchResult, setSearchResult] = useState([]);
  const [resultCount, setResultCount] = useState(0);

  const [searchHistory, setSearchHistory] = useState([]);
  const [searchSuggestion, setSearchSuggestion] = useState([]);
  const [showSearchSuggestion, setShowSearchSuggestion] = useState(false);

  const [finalSearchTerm, setFinalSearchTerm] = useState("");

  const selectedReportType = options.find((opt) => opt.is_selected);

  // Initial API calls on mount
  useEffect(() => {
    getTrendingKeywords();
    getRecentlyViewedData();
  }, []);

  // Debounced suggestions trigger
  useEffect(() => {
    if (searchInput.length >= 3) {
      debouncedFetchSuggestions(searchInput);
    } else if (searchInput.length === 0) {
      fetchSearchHistory();
    }
  }, [searchInput]);

  useEffect(() => {
    if (finalSearchTerm && finalSearchTerm.length >= 3) {
      getSearchResults(finalSearchTerm);
    } else {
      setSearchResult([]);
    }
  }, [
    finalSearchTerm,
    selectedReportType,
    selectedDateFilter,
    filterByTypeFilter,
  ]);

  // Fetch trending keywords
  const getTrendingKeywords = async (params = {}) => {
    const defaultParams = {
      page_size: 10,
      page: 1,
      sort_order: "desc",
      sort_column: "records",
      search_column: "keyword",
      start_date: moment().subtract(1, "month").format("YYYY-MM-DD"),
      end_date: moment().format("YYYY-MM-DD"),
    };

    const queryParams = new URLSearchParams({
      ...defaultParams,
      ...params,
    }).toString();

    try {
      await OpenSearchService.getTrendingKeywords(queryParams)
        .then((res) => {
          const keywords = res?.data?.data?.map((item) => item.keyword) || [];
          setTrendingKeywords(keywords);
        })
        .catch((error) => {
          Toast(
            error?.data?.error || constants.ERROR.SOMETHING_WENT_WRONG,
            "error"
          );
        });
    } catch (error) {
      console.error("Error fetching trending keywords:", error);
    }
  };

  // Fetch recent view
  const getRecentlyViewedData = async () => {
    try {
      await OpenSearchService.getRecentlyViewed()
        .then((res) => {
          setRecentViewData(res?.data?.result || []);
        })
        .catch((error) => {
          Toast(
            error?.data?.error || constants.ERROR.SOMETHING_WENT_WRONG,
            "error"
          );
        });
    } catch (error) {
      console.error("Error fetching recently viewed:", error);
    }
  };

  // Fetch search history
  const fetchSearchHistory = async () => {
    try {
      const body = {
        page_size: 0,
        page: 0,
        sort_order: "desc",
        sort_column: "id",
      };
      const res = await OpenSearchService.fetchSearchHistory(body);
      const keywords = [...new Set(res?.data?.data?.map((ele) => ele.keyword))];
      setSearchHistory(keywords);
      setSearchSuggestion(keywords);
    } catch (error) {
      console.error("Error fetching search history:", error);
    }
  };

  // Debounced suggestion fetcher
  const debouncedFetchSuggestions = useMemo(
    () =>
      debounce((input) => {
        const body = { search: input };
        OpenSearchService.fetchSearchSuggestions(body)
          .then((res) => {
            const suggestions = [...new Set(res?.data?.highlights)];
            setSearchSuggestion(suggestions);
          })
          .catch((error) => console.error("Suggestion fetch error:", error));
      }, 800),
    []
  );

  // Fetch results based on final selection
  const getSearchResults = async (query) => {
    const body = {
      search: query,
      page: 1,
      pageSize: 10,
    };

    if (selectedReportType?.value) body.type = [selectedReportType.value];
    if (selectedDateFilter) body.publishedAt = selectedDateFilter;

    if (filterByTypeFilter && filterByTypeFilter !== "all") {
      body.method = filterByTypeFilter;
    }
    await OpenSearchService.getSearchResults(body)
      .then((res) => {
        setSearchResult(res?.data?.data || []);
        setResultCount(res?.data?.meta?.total || 0);
      })
      .catch((error) => console.error("Search result error:", error));
  };

  // Dropdown & Filter change handlers
  const handleCompanyResearchOptionsChange = (selectedItem) => {
    const updatedOptions = options.map((opt) => ({
      ...opt,
      is_selected: opt.id === selectedItem.id,
    }));
    setOptions(updatedOptions);
  };

  const handleLastUpdatedChange = (selected) => {
    setSelectedDateFilter(selected.value);
  };

  const onFilterByTypeChange = (selected) => {
    const updated = filterByTypeOptions.map((opt) => ({
      ...opt,
      is_selected: opt.label === selected.label,
    }));
    setFilterByTypeFilter(selected.value);
    setFilterByTypeOptions(updated);
  };

  // Search redirection
  const searchOnGlobal = () => {
    history.push({
      pathname: `/search`,
      search: `?q=${encodeURIComponent(searchInput)}`,
      state: {
        report_type: selectedReportType || "",
        published_date: selectedDateFilter || "",
        file_filter: filterByTypeFilter || "",
      },
    });
  };

  // Click handlers for history/suggestion
  const handleHistoryClick = (item) => {
    setFinalSearchTerm(item);
    setShowSearchSuggestion(false);
  };

  const handleSuggestionClick = (item) => {
    setFinalSearchTerm(item);
    setSearchInput(item);
    setShowSearchSuggestion(false);
  };

  return (
    <div className="open-search-container">
      <SearchBar
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        options={options}
        selected={selectedReportType}
        onCompanyResearchOptionsChange={handleCompanyResearchOptionsChange}
        showSearchSuggestions={showSearchSuggestion}
        searchHistory={searchHistory}
        searchSuggestion={searchSuggestion}
        onSearchFocus={() => setShowSearchSuggestion(true)}
        onCloseModal={() => setShowSearchSuggestion(false)}
        onHistoryClick={handleHistoryClick}
        onSuggestionClick={handleSuggestionClick}
        setShowSearchSuggestion={setShowSearchSuggestion}
      />

      <div className="open-search-content">
        <div className="left-section">
          {searchInput.length < 1 ? (
            <RecentView recentViewData={recentViewData} />
          ) : (
            <div className="result-section">
              <ResultList resultCount={resultCount} results={searchResult} />
              <span onClick={searchOnGlobal} className="view-all">
                VIEW ALL RESULTS
              </span>
            </div>
          )}
        </div>

        <div className="right-section">
          <div className="filter-section">
            <FilterOptions
              trendingKeyWords={trendingKeywords}
              publishedDateOptions={PUBLISHED_DATE_OPTIONS}
              dateFilter={selectedDateFilter}
              onLastUpdatedChange={handleLastUpdatedChange}
              filterByTypeoptions={filterByTypeOptions}
              filterByTypeFilter={filterByTypeFilter}
              onFilterByTypeChange={onFilterByTypeChange}
              setTrendingKeywordSearch={setFinalSearchTerm}
              setSearchInput={setSearchInput}
            />
          </div>
        </div>
      </div>

      <div className="footer">
        <GotoAll />
        <Legends />
      </div>
    </div>
  );
};

export default OpenSearch;
